import React, { useState, useEffect } from 'react';
import './CollectionsBlock.scss';
import { useCollectionCounts } from './useCollectionCounts';
import {
  DEFAULT_COLLECTION_DEFINITIONS,
  DEFAULT_COLLECTIONS_CONFIG,
  fetchStaticCollectionCounts,
} from './CollectionsService';
import type {
  CollectionDefinition,
  CollectionsApiConfig,
} from './CollectionsService';
import { environment } from 'nsdi-volto-frontend/config/environments';

interface CollectionItem {
  id?: string;
  title: string;
  itemCount?: number;
  link?: string;
}

interface CollectionItemWithId {
  id: string;
  title: string;
  itemCount: number;
  link?: string;
}

interface CollectionsBlockProps {
  data?: {
    title?: string;
    collections?: CollectionItem[];
    showItemCount?: boolean;
    maxItemsToShow?: number;
    // API configuration
    voyagerBaseUrl?: string;
    voyagerConfigId?: string;
    // Whether to fetch data dynamically
    fetchDynamic?: boolean;
    // Custom collection definitions for dynamic fetching
    collectionDefinitions?: CollectionDefinition[];
    // Base URL for search results page (static collections)
    searchResultsBaseUrl?: string;
  };
  block?: string;
  onChangeBlock?: (blockId: string, data: any) => void;
}

const CollectionIcon: React.FC = () => (
  <svg
    className="collection-icon"
    viewBox="0 0 24 24"
    width="24"
    height="24"
    aria-hidden="true"
  >
    {/* Stacked documents - back layers */}
    <rect
      x="3"
      y="5"
      width="14"
      height="17"
      rx="1"
      fill="#e0e0e0"
      stroke="#9e9e9e"
      strokeWidth="1"
    />
    <rect
      x="5"
      y="3"
      width="14"
      height="17"
      rx="1"
      fill="#f5f5f5"
      stroke="#9e9e9e"
      strokeWidth="1"
    />
    {/* Front document */}
    <rect
      x="7"
      y="1"
      width="14"
      height="17"
      rx="1"
      fill="#fff"
      stroke="#9e9e9e"
      strokeWidth="1"
    />
    {/* Color accent bars on front document */}
    <rect x="9" y="4" width="2.5" height="11" fill="#1976d2" />
    <rect x="12.5" y="4" width="2.5" height="11" fill="#43a047" />
    <rect x="16" y="4" width="2.5" height="11" fill="#fb8c00" />
  </svg>
);

/**
 * Format number with commas for thousands separator
 */
const formatNumber = (num: number): string => {
  return num.toLocaleString('en-US');
};

const CollectionsBlock: React.FC<CollectionsBlockProps> = ({
  data,
  block,
  onChangeBlock,
}) => {
  const title = data?.title || 'Collections';
  const showItemCount = data?.showItemCount !== false;
  const maxItemsToShow = data?.maxItemsToShow || 10;
  const fetchDynamic = data?.fetchDynamic === true; // Default to false (static)
  const searchResultsBaseUrl =
    data?.searchResultsBaseUrl || environment.voyager.searchUrl;

  // API configuration from block data or defaults
  const apiConfig: CollectionsApiConfig = {
    baseUrl: data?.voyagerBaseUrl || DEFAULT_COLLECTIONS_CONFIG.baseUrl,
    configId: data?.voyagerConfigId || DEFAULT_COLLECTIONS_CONFIG.configId,
  };

  // Collection definitions - use custom or defaults
  const collectionDefinitions =
    data?.collectionDefinitions || DEFAULT_COLLECTION_DEFINITIONS;

  // Fetch dynamic counts from API (for dynamic mode)
  const {
    collections: dynamicCollections,
    loading: dynamicLoading,
    error: dynamicError,
  } = useCollectionCounts(collectionDefinitions, apiConfig, fetchDynamic);

  // State for static collections with fetched counts
  const [staticCollections, setStaticCollections] = useState<
    CollectionItemWithId[]
  >([]);
  const [, setStaticLoading] = useState<boolean>(false);
  const [staticError, setStaticError] = useState<string | null>(null);

  // Determine if we're in edit mode (can save changes)
  const isEditMode = !!(block && onChangeBlock);

  // Fetch counts for static collections - only in edit mode
  // In view mode, just use the stored values from block data
  useEffect(() => {
    const fetchStaticCounts = async () => {
      // Only fetch if we have static collections configured
      if (!data?.collections || data.collections.length === 0) {
        setStaticCollections([]);
        return;
      }

      // Skip if using dynamic mode
      if (fetchDynamic) {
        return;
      }

      // In view mode: don't fetch, just use stored values
      if (!isEditMode) {
        setStaticCollections(
          data.collections.map((c, i) => ({
            id: c.id || `collection-${i}`,
            title: c.title,
            link: c.link,
            itemCount: c.itemCount || 0,
          })),
        );
        return;
      }

      // In edit mode: fetch fresh counts
      setStaticLoading(true);
      setStaticError(null);

      try {
        const collectionsWithCounts = await fetchStaticCollectionCounts(
          data.collections,
          apiConfig.baseUrl,
        );
        setStaticCollections(collectionsWithCounts);

        // Save fetched counts back to block data so they appear in the edit fields
        if (block && onChangeBlock) {
          const updatedCollections = data.collections.map((c, i) => {
            const fetched = collectionsWithCounts.find(
              (fc) =>
                fc.title === c.title || fc.id === (c.id || `collection-${i}`),
            );
            return {
              ...c,
              itemCount: fetched?.itemCount ?? c.itemCount ?? 0,
            };
          });

          // Only update if counts have changed
          const hasChanges = updatedCollections.some((c, i) => {
            const original = data.collections?.[i];
            return original && c.itemCount !== original.itemCount;
          });

          if (hasChanges) {
            onChangeBlock(block, {
              ...data,
              collections: updatedCollections,
            });
          }
        }
      } catch (err) {
        setStaticError(
          err instanceof Error ? err.message : 'Failed to fetch counts',
        );
        // Use original collections without counts on error
        setStaticCollections(
          data.collections.map((c, i) => ({
            id: c.id || `collection-${i}`,
            title: c.title,
            link: c.link,
            itemCount: c.itemCount || 0,
          })),
        );
      } finally {
        setStaticLoading(false);
      }
    };

    fetchStaticCounts();
  }, [data, block, onChangeBlock, apiConfig.baseUrl, fetchDynamic, isEditMode]);

  // Determine loading state - disabled for now to avoid shimmer in view mode
  // The loading shimmer was confusing users - just show actual values
  const showLoadingState = false; // Never show loading shimmer
  const error = fetchDynamic ? dynamicError : staticError;

  // Determine which collections to display:
  // 1. If custom collections are provided in data, use those (prefer fetched counts over stored)
  // 2. If dynamic fetch is enabled and successful, use dynamic data
  let collections: CollectionItemWithId[];

  if (data?.collections && data.collections.length > 0) {
    // User-provided collections from block config
    // Use fetched counts if available, otherwise use stored counts from block data
    if (staticCollections.length > 0) {
      collections = staticCollections;
    } else {
      // Use stored values from block data immediately (no waiting for fetch)
      collections = data.collections.map((c, i) => ({
        id: c.id || `collection-${i}`,
        title: c.title,
        link: c.link,
        itemCount: c.itemCount || 0,
      }));
    }
  } else if (fetchDynamic && !dynamicLoading && dynamicCollections.length > 0) {
    // Dynamic collections from API
    collections = dynamicCollections;
  } else {
    // No collections configured
    collections = [];
  }

  const displayedCollections = collections.slice(0, maxItemsToShow);

  const handleCollectionClick = (collection: CollectionItemWithId) => {
    if (collection.link) {
      let url = collection.link.trim();
      // If it's an absolute URL, use as-is
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.location.href = url;
        return;
      }
      // Otherwise treat as query parameters - append to base URL
      // Remove leading ? if present
      const queryParams = url.startsWith('?') ? url.slice(1) : url;
      window.location.href = `${searchResultsBaseUrl}?${queryParams}`;
    }
  };

  return (
    <div className="collections-block-wrapper">
      <h2 className="collections-title">{title}</h2>
      <div className={`collections-block ${showLoadingState ? 'loading' : ''}`}>
        {error && (
          <div className="collections-error" role="alert">
            <small>Unable to load live counts. Showing cached data.</small>
          </div>
        )}
        {displayedCollections.length === 0 && !showLoadingState ? (
          <div className="collections-empty">
            <p>
              No collections configured. Add collections in the block settings.
            </p>
          </div>
        ) : (
          <ul className="collections-list">
            {displayedCollections.map((collection) => (
              <li key={collection.id} className="collection-item">
                <button
                  type="button"
                  className="collection-item-button"
                  onClick={() => handleCollectionClick(collection)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleCollectionClick(collection);
                    }
                  }}
                >
                  <div className="collection-icon-wrapper">
                    <CollectionIcon />
                  </div>
                  <div className="collection-content">
                    <span className="collection-name">{collection.title}</span>
                    {showItemCount && (
                      <span className="collection-count">
                        {formatNumber(collection.itemCount)} items
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CollectionsBlock;
